import { useInfiniteQuery } from '@tanstack/react-query'

import { AdminListResponse } from '@engine/models/admin'

import { adminFetch, type FetchResourceParams } from '#api/client'
import { retryUnlessClientError, sanitizeParams } from '#api/query'

export interface UseInfiniteAdminResourceOptions {
  autoRefreshMs?: number
  enabled?: boolean
}

export interface UseInfiniteAdminResourceResult<T> {
  items: T[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error?: string
  total?: number
  lastUpdated?: Date
  refresh: () => Promise<void>
  fetchNextPage: () => Promise<void>
}

export function useInfiniteAdminResource<T extends Record<string, any>>(
  resource: string,
  params: FetchResourceParams,
  options: UseInfiniteAdminResourceOptions = {},
): UseInfiniteAdminResourceResult<T> {
  const { autoRefreshMs, enabled = true } = options
  const sanitizedParams = sanitizeParams(params)

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [resource, JSON.stringify(sanitizedParams)],
    queryFn: async ({ signal, pageParam }) => {
      const queryParams = {
        ...sanitizedParams,
        ...(pageParam ? { cursor: pageParam } : {}),
      }

      const response = await adminFetch<AdminListResponse<T>>(resource, {
        params: queryParams,
        signal,
      })
      return response
    },
    enabled,
    refetchInterval: autoRefreshMs,
    retry: retryUnlessClientError,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      // Use the continuation token provided by the server
      return lastPage.nextCursor ?? undefined
    },
  })

  // Flatten all pages into a single array
  const allItems = data?.pages.flatMap(page => page.items ?? []) ?? []

  return {
    items: allItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    error: error?.message,
    total: data?.pages[0]?.total,
    lastUpdated: data ? new Date() : undefined,
    refresh: () => refetch().then(() => {}),
    fetchNextPage: () => fetchNextPage().then(() => {}),
  }
}
